import React, { memo, MouseEvent, ReactNode, useContext, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { Flame } from "lucide-react";
import { LanguageNameSpaces, TradeData, TradeDataWithKey } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { getAssetFromSymbol } from "@shared/utils/misc";
import { AccountObjectType } from "@shared/features/settings/types";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import { CheckboxData } from "@shared/common/CardsList/types";
import PriceLadder from "common/NewLook/PriceLadder.tsx";
import { isTradeBarModel } from "common/NewLook/tradeBarLayout.ts";
import { PriceLadderModel } from "common/NewLook/priceLadder.ts";
import {
  directionColor,
  getNewLookTokens,
  getPanelShadow,
  NEW_LOOK_FIGURE_WEIGHT,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import LongPress from "common/Gestures/LongPress.tsx";
import Checkbox from "common/Checkbox/Checkbox";
import { DrawerContext } from "common/Drawers/Drawer.context";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import { useTradeCard } from "features/trades/common/commonHooks";
import TradeActionsDropdown from "features/trades/OpenTrades/TradeActionsDropdown";
import PnlSocketDataProvider, {
  usePnlSocketData,
} from "features/trades/TradesCards/PnlSocketDataProvider.tsx";
import {
  formatTimeInTrade,
  useLadderPriceFormat,
  useTradeRowLadder,
} from "features/trades/NewLook/useTradeLadder.ts";
import { useLadderCaption } from "features/trades/NewLook/useLadderCaption.ts";
import useMoneyFormat, {
  formatSignedPercent,
} from "features/trades/NewLook/useMoneyFormat.ts";
import {
  phoneCompactMoney,
  phoneMoney,
} from "features/dashboard/PhoneDashboard/PhoneSummary";
import {
  getExitKind,
  getPhoneLadderEnds,
  getPhoneLadderLegend,
  isWaitingForEntry,
  PhoneExitKind,
  shortPrice,
  PhoneLegendItem,
} from "features/trades/NewLook/phoneTradeCardFigures.ts";
import PhoneTradeDetails from "features/trades/NewLook/PhoneTradeDetails.tsx";
import {
  PhoneCardSlot,
  PhoneMetaPair,
  PhonePill,
  PhoneSkeleton,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import {
  TRADE_CARD_ACTIONS_ID,
  TRADE_CARD_EXPAND_ID,
  TRADE_CARD_LADDER_END_ID,
  TRADE_CARD_LADDER_START_ID,
  TRADE_CARD_ROW_ID,
  TRADE_CARD_SIDE_ID,
  TRADE_CARD_SELECT_ID,
  TRADE_CARD_POSITION_ID,
} from "testIds";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const DETAILS_GAP = { paddingTop: 12 };
const FIGURE_MIN_FONT_SIZE = 9;
const MISSING_PRICE = "—";
const FOOTER_SEPARATOR = " · ";
const MISSING_LADDER_LABELS: Partial<
  Record<PhoneLegendItem["key"], TranslationKeys<LanguageNameSpaces.TRADES>>
> = {
  stop: "phone.card_no_stop",
  take_profit: "phone.card_no_take_profit",
};
const EXIT_LABELS: Record<
  PhoneExitKind,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  exit: "phone.card_exit",
  stopped: "phone.card_stopped",
  liquidated: "phone.card_liquidated",
};
const LADDER_LABELS: Record<
  PhoneLegendItem["key"],
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  entry: "phone.card_entry",
  next_entry: "phone.card_entry",
  price: "phone.card_price",
  take_profit: "phone.card_take_profit",
  stop: "phone.card_stop",
  low: "phone.card_low",
  high: "phone.card_high",
};

interface PhoneTradeRowProps {
  tradeData: TradeDataWithKey;
  isOpenTrades: boolean;
  expanded: boolean;
  onToggleExpanded: (tradeId: number) => void;
  allowTradeClick?: boolean;
  shouldFetchSocketPnl?: boolean;
  showBulkActions?: boolean;
  hideActions?: boolean;
  inset?: boolean;
}

type CardBodyProps = Omit<PhoneTradeRowProps, "shouldFetchSocketPnl" | "inset">;

type TradeCardHelpers = ReturnType<typeof useTradeCard>;

interface TradeAmounts {
  amount: number;
  quantity: ReactNode;
  pnl: number;
  percent?: number;
  time?: string;
}

interface TradeFigures {
  merged: TradeData;
  model: PriceLadderModel;
  formatPrice: (price: number) => string;
  caption: { text: string; isWarning?: boolean };
  legend: PhoneLegendItem[];
  amounts: TradeAmounts;
}

type LadderFigures = Pick<TradeFigures, "model" | "formatPrice" | "legend">;

interface CardContentProps extends CardBodyProps {
  figures: TradeFigures;
  checkbox?: CheckboxData;
  onToggle: () => void;
  onOpenDetails?: () => void;
}

const PhoneTradeRow = ({
  shouldFetchSocketPnl = true,
  inset = true,
  ...props
}: PhoneTradeRowProps) => (
  <PhoneCardSlot $flush={!inset}>
    <PnlSocketDataProvider
      tradeId={props.tradeData.id}
      skip={!shouldFetchSocketPnl}
    >
      <CardBody {...props} />
    </PnlSocketDataProvider>
  </PhoneCardSlot>
);

export default memo(PhoneTradeRow);

const CardBody = (props: CardBodyProps) => {
  const state = useCardState(props);
  const card = (
    <Card
      data-testid={TRADE_CARD_ROW_ID}
      $selected={!!state.checkbox?.isSelected}
      onClick={state.onToggle}
    >
      <CardContent {...props} {...state} />
    </Card>
  );
  if (!state.onOpenDetails) return card;
  return (
    <LongPress
      triggerElement={card}
      onLongPress={state.onOpenDetails}
      ignoreControls={true}
    />
  );
};

const CardContent = ({
  figures,
  checkbox,
  onOpenDetails,
  ...props
}: CardContentProps) => (
  <>
    <CardHead {...props} figures={figures} checkbox={checkbox} />
    <ExpandButton expanded={props.expanded} onToggle={props.onToggle} />
    {props.isOpenTrades ? (
      <OpenProgress figures={figures} />
    ) : (
      <ClosedProgress figures={figures} />
    )}
    <ExpandCollapse
      expanded={props.expanded}
      animateAuto={false}
      style={DETAILS_GAP}
    >
      <PhoneTradeDetails
        trade={figures.merged}
        model={figures.model}
        isOpenTrades={props.isOpenTrades}
        formatPrice={figures.formatPrice}
        caption={props.isOpenTrades ? getLadderCaption(figures) : undefined}
        duration={figures.amounts.time}
        quantity={
          <>
            {figures.amounts.quantity}{" "}
            {getAssetFromSymbol(props.tradeData.symbol)}
          </>
        }
        leverage={getPositionTerms(props.tradeData)}
        onOpenDetails={onOpenDetails}
      />
    </ExpandCollapse>
  </>
);

const CardHead = ({
  figures,
  checkbox,
  ...props
}: Omit<CardContentProps, "onOpenDetails">) => (
  <Head>
    {checkbox && <SelectBox checkbox={checkbox} />}
    <IconWithSource
      symbol={props.tradeData.symbol}
      sourceType={props.tradeData.source_type}
      source={props.tradeData.group}
      mainIconSize={32}
      style={ICON_STYLE}
    />
    <Names>
      <SymbolLine
        tradeData={props.tradeData}
        isOpenTrades={props.isOpenTrades}
      />
      <AccountLine tradeData={props.tradeData} amounts={figures.amounts} />
    </Names>
    {props.isOpenTrades && isWaitingForEntry(figures.model) ? (
      <WaitingFigure figures={figures} />
    ) : (
      <PnlFigure amounts={figures.amounts} />
    )}
    <CardActions {...props} hidden={!!checkbox} />
  </Head>
);

const SelectBox = ({ checkbox }: { checkbox: CheckboxData }) => (
  <CheckboxHit
    data-testid={TRADE_CARD_SELECT_ID}
    onClick={(event) => stopAnd(event, checkbox.onClick)}
  >
    <Checkbox checked={checkbox.isSelected} $size={18} />
  </CheckboxHit>
);

const SymbolLine = ({
  tradeData,
  isOpenTrades,
}: Pick<CardBodyProps, "tradeData" | "isOpenTrades">) => {
  const tokens = getNewLookTokens(useTheme());
  return (
    <SymbolRow>
      <InactiveAccountIcon
        accountId={tradeData.client_id}
        objectType={AccountObjectType.TRADE}
        hideIcon={true}
        placement="topLeft"
        style={SYMBOL_STYLE}
      >
        <SymbolText title={tradeData.symbol}>{tradeData.symbol}</SymbolText>
      </InactiveAccountIcon>
      {isOpenTrades && tradeData.closest_order?.is_hot && (
        <Flame
          size={14}
          strokeWidth={2}
          color={tokens.stop}
          style={FLEX_NONE}
        />
      )}
      {tradeData["Long/Short"] && (
        <PhonePill
          $tone={tradeData["Long/Short"] === "Short" ? "red" : "green"}
          data-testid={TRADE_CARD_SIDE_ID}
        >
          {translateOptionName(tradeData["Long/Short"])}
        </PhonePill>
      )}
    </SymbolRow>
  );
};

const AccountLine = ({
  tradeData,
  amounts,
}: {
  tradeData: TradeData;
  amounts: TradeAmounts;
}) => {
  const money = usePhoneMoney();
  const { symbol } = useMoneyFormat();
  return (
    <Meta data-testid={TRADE_CARD_POSITION_ID}>
      <PhoneMetaPair
        lead={tradeData.account}
        tail={money(amounts.amount)}
        compactTail={phoneCompactMoney(amounts.amount, symbol)}
      />
    </Meta>
  );
};

const PnlFigure = ({ amounts }: { amounts: TradeAmounts }) => {
  const tokens = getNewLookTokens(useTheme());
  const money = usePhoneMoney();
  return (
    <Amounts>
      <Pnl style={{ color: directionColor(tokens, amounts.pnl) }}>
        {money(amounts.pnl, true)}
      </Pnl>
      {amounts.percent !== undefined && (
        <Percent style={{ color: directionColor(tokens, amounts.percent) }}>
          {formatSignedPercent(amounts.percent)}
        </Percent>
      )}
    </Amounts>
  );
};

const WaitingFigure = ({ figures }: { figures: TradeFigures }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const entry = figures.legend.find((item) => item.key === "next_entry");
  return (
    <Amounts>
      <Waiting>{t("phone.card_waiting")}</Waiting>
      {entry?.price !== undefined && (
        <Percent $muted={true}>
          {t("phone.card_entry")} {shortPrice(entry.price, figures.formatPrice)}
        </Percent>
      )}
    </Amounts>
  );
};

export const PhoneTradeLadder = ({
  trade,
  model,
  formatPrice,
  priceChange,
}: {
  trade: TradeData;
  model: PriceLadderModel;
  formatPrice: (price: number) => string;
  priceChange?: number;
}) => {
  const legend = useMemo(
    () => getPhoneLadderLegend(trade, model),
    [trade, model]
  );
  return (
    <OpenProgress
      figures={{ model, formatPrice, legend }}
      priceChange={priceChange}
    />
  );
};

const OpenProgress = ({
  figures,
  priceChange,
}: {
  figures: LadderFigures;
  priceChange?: number;
}) => {
  const tokens = getNewLookTokens(useTheme());
  const [start, end] = getPhoneLadderEnds(figures.legend, figures.model);
  const hasTradeBar =
    figures.model.type === "levels" && isTradeBarModel(figures.model);
  return (
    <LadderBlock
      $priced={figures.model.type !== "split"}
      $hasTradeBar={hasTradeBar}
    >
      <PriceLadder
        model={figures.model}
        formatPrice={figures.formatPrice}
        background={tokens.panel}
        showPrice={true}
        showLabels={hasTradeBar}
        priceChange={priceChange}
      />
      {!hasTradeBar && start && end && (
        <LadderEnds>
          <LadderEnd item={start} figures={figures} isEnd={false} />
          <LadderEnd item={end} figures={figures} isEnd={true} />
        </LadderEnds>
      )}
    </LadderBlock>
  );
};

const LadderEnd = ({
  item,
  figures,
  isEnd,
}: {
  item: PhoneLegendItem;
  figures: LadderFigures;
  isEnd: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const missingLabel = MISSING_LADDER_LABELS[item.key];
  return (
    <EndLabel
      $isEnd={isEnd}
      data-testid={
        isEnd ? TRADE_CARD_LADDER_END_ID : TRADE_CARD_LADDER_START_ID
      }
    >
      <AutoSizeText inheritFont={true} minFontSizePx={FIGURE_MIN_FONT_SIZE}>
        {item.price === undefined && missingLabel ? (
          <EndName>{t(missingLabel)}</EndName>
        ) : (
          <>
            <EndName>{t(LADDER_LABELS[item.key])}</EndName>{" "}
            {item.price === undefined
              ? MISSING_PRICE
              : shortPrice(item.price, figures.formatPrice)}
            {item.distance !== undefined && (
              <>
                {" "}
                <EndDistance>{formatSignedPercent(item.distance)}</EndDistance>
              </>
            )}
          </>
        )}
      </AutoSizeText>
    </EndLabel>
  );
};

const ClosedProgress = ({ figures }: { figures: TradeFigures }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { merged, formatPrice } = figures;
  const exitKind = getExitKind(merged);
  return (
    <ClosedEnds>
      <ClosedEnd $isEnd={false} data-testid={TRADE_CARD_LADDER_START_ID}>
        <AutoSizeText inheritFont={true} minFontSizePx={FIGURE_MIN_FONT_SIZE}>
          <PriceName $alert={false}>{t("phone.card_entry")}</PriceName>{" "}
          {formatOptionalPrice(merged.entry_price, formatPrice)}
        </AutoSizeText>
      </ClosedEnd>
      <ClosedEnd
        $isEnd={true}
        data-testid={TRADE_CARD_LADDER_END_ID}
        data-tone={exitKind}
      >
        <AutoSizeText inheritFont={true} minFontSizePx={FIGURE_MIN_FONT_SIZE}>
          <PriceName $alert={exitKind !== "exit"}>
            {t(EXIT_LABELS[exitKind])}
          </PriceName>{" "}
          {formatOptionalPrice(merged.price_when_closed, formatPrice)}
        </AutoSizeText>
      </ClosedEnd>
    </ClosedEnds>
  );
};

const ExpandButton = ({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const label = t(
    expanded ? "phone.card_hide_details" : "phone.card_show_details"
  );
  return (
    <HiddenExpandButton
      type="button"
      aria-expanded={expanded}
      onClick={(event) => stopAnd(event, onToggle)}
      data-testid={TRADE_CARD_EXPAND_ID}
    >
      {label}
    </HiddenExpandButton>
  );
};

const CardActions = ({
  tradeData,
  isOpenTrades,
  showBulkActions,
  hideActions,
  hidden,
}: Pick<
  CardBodyProps,
  "tradeData" | "isOpenTrades" | "showBulkActions" | "hideActions"
> & { hidden: boolean }) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  return (
    <Actions data-testid={TRADE_CARD_ACTIONS_ID}>
      {!isDrawer && !hideActions && !hidden && (
        <TradeActionsDropdown
          tradeData={tradeData}
          isCardsView={true}
          isOpenTrades={isOpenTrades}
          showBulkActions={showBulkActions}
        />
      )}
    </Actions>
  );
};

export const PhoneTradeRowSkeleton = ({
  inset = true,
}: {
  inset?: boolean;
}) => (
  <PhoneCardSlot aria-hidden={true} $flush={!inset}>
    <Card>
      <Head>
        <PhoneSkeleton $width={32} $height={32} $radius={16} />
        <Names>
          <PhoneSkeleton $width={112} $height={13} />
          <PhoneSkeleton
            $width={150}
            $height={10}
            style={SKELETON_META_STYLE}
          />
        </Names>
        <PhoneSkeleton $width={60} $height={14} />
      </Head>
      <LadderBlock $priced={true}>
        <PhoneSkeleton $width={400} $height={6} $radius={3} />
        <LadderEnds>
          <PhoneSkeleton $width={56} $height={11} />
          <PhoneSkeleton $width={56} $height={11} />
        </LadderEnds>
      </LadderBlock>
    </Card>
  </PhoneCardSlot>
);

const useCardState = (props: CardBodyProps) => {
  const helpers = useTradeCard({ allowTradeClick: props.allowTradeClick });
  const figures = usePhoneTradeFigures(props.tradeData, props.isOpenTrades);
  const checkbox = useCardCheckbox(props.tradeData);
  const displayTradeDetails = useDisplayTradeDetails();
  const { tradeData, allowTradeClick } = props;
  return {
    figures: { ...figures, amounts: getTradeAmounts(figures.merged, helpers) },
    checkbox,
    onToggle: () => props.onToggleExpanded(tradeData.id),
    onOpenDetails:
      allowTradeClick && displayTradeDetails
        ? () => helpers.openTradeExtraDetails(tradeData)
        : undefined,
  };
};

const usePhoneTradeFigures = (
  tradeData: TradeData,
  isOpenTrades: boolean
): Omit<TradeFigures, "amounts"> => {
  const live = usePnlSocketData() as Partial<TradeData> | undefined;
  const merged = useMemo(
    () => (live ? { ...tradeData, ...live } : tradeData) as TradeData,
    [tradeData, live]
  );
  const model = useTradeRowLadder(tradeData, live, isOpenTrades);
  const formatPrice = useLadderPriceFormat(
    tradeData.symbol,
    tradeData.exchange
  );
  const caption = useLadderCaption(model, formatPrice);
  const legend = useMemo(
    () => getPhoneLadderLegend(merged, model),
    [merged, model]
  );
  return { merged, model, formatPrice, caption, legend };
};

const getTradeAmounts = (
  trade: TradeData,
  helpers: TradeCardHelpers
): TradeAmounts => {
  const graph = trade.graph_data;
  const profit = graph && "profit" in graph ? graph.profit : undefined;
  return {
    amount: trade[helpers.allocatedHeader] || 0,
    quantity: helpers.getTradeQuantity(trade),
    pnl: Number(trade[helpers.amountChangeHeader]) || 0,
    percent: profit === undefined ? undefined : Number(profit),
    time: formatTimeInTrade(graph?.time_passed),
  };
};

const useCardCheckbox = (tradeData: TradeData) => {
  const { getCheckboxProps, isEditMode } =
    useContext<ContextProps<TradeData> | undefined>(CardsSelectorContext) || {};
  return isEditMode ? getCheckboxProps?.(tradeData) : undefined;
};

const usePhoneMoney = () => {
  const { symbol, precision } = useMoneyFormat();
  return (value: number, signed = false) =>
    phoneMoney(value, symbol, precision, signed);
};

const getPositionTerms = (trade: TradeData) =>
  trade.leverage
    ? [`${trade.leverage}x`, translateOptionName(trade.margin_type)]
        .filter(Boolean)
        .join(FOOTER_SEPARATOR)
    : undefined;

const getLadderCaption = ({ model, caption }: TradeFigures) =>
  model.type === "split" ||
  (model.type === "grid" && model.cycles !== undefined && !caption.isWarning)
    ? caption.text
    : undefined;

const formatOptionalPrice = (
  value: unknown,
  formatPrice: (price: number) => string
) => {
  const price = Number(value);
  return isFinite(price) && price > 0 ? formatPrice(price) : MISSING_PRICE;
};

const stopAnd = (event: MouseEvent, action: () => void) => {
  event.stopPropagation();
  action();
};

const ICON_STYLE = { flex: "none", marginRight: 2 };
const SYMBOL_STYLE = { minWidth: 0, maxWidth: "100%" };
const FLEX_NONE = { flex: "none" };
const SKELETON_META_STYLE = { marginTop: 7 };

const HiddenExpandButton = styled.button`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
const Card = styled.div<{ $selected?: boolean }>`
  position: relative;
  padding: 14px 16px 12px;
  user-select: none;
  -webkit-touch-callout: none;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  ${(p) =>
    p.$selected &&
    css`
      box-shadow: inset 0 0 0 1.5px ${p.theme.colors.primaryBlue};
    `}
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 150ms;
  &:active {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:has(${HiddenExpandButton}:focus-visible) {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
  }
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const CheckboxHit = styled.div`
  flex: none;
  display: flex;
  margin: -6px -2px -6px -6px;
  padding: 6px;
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
`;
const SymbolRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  min-width: 0;
  height: 20px;
  overflow: hidden;
`;
const SymbolText = styled.span`
  display: block;
  min-width: 0;
  ${newLookPhoneType.cardTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Meta = styled.div`
  min-width: 0;
  margin-top: 2px;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Amounts = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Pnl = styled.span`
  ${newLookPhoneType.rowFigure};
  white-space: nowrap;
`;
const Percent = styled.span<{ $muted?: boolean }>`
  margin-top: 2px;
  ${newLookPhoneType.meta};
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  white-space: nowrap;
  color: ${(p) => (p.$muted ? getNewLookTokens(p.theme).muted : "inherit")};
`;
const Waiting = styled.span`
  ${newLookPhoneType.label};
  font-weight: 500;
  line-height: 20px;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  white-space: nowrap;
`;
const LadderBlock = styled.div<{ $priced: boolean; $hasTradeBar?: boolean }>`
  margin-top: ${(p) => (p.$priced && !p.$hasTradeBar ? 28 : 12)}px;
`;
const LadderEnds = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;
const EndLabel = styled.div<{ $isEnd: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  ${newLookPhoneType.meta};
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: ${(p) => (p.$isEnd ? "right" : "left")};
`;
const EndDistance = styled.span`
  margin-left: 3px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const EndName = styled.span`
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const ClosedEnds = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
`;
const ClosedEnd = styled.div<{ $isEnd: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13.5px;
  line-height: 18px;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: ${(p) => (p.$isEnd ? "right" : "left")};
`;
const PriceName = styled.span<{ $alert: boolean }>`
  font-size: 12px;
  font-weight: ${(p) => (p.$alert ? 500 : 400)};
  color: ${(p) =>
    p.$alert ? p.theme.colors.orange : getNewLookTokens(p.theme).secondary};
`;
const Actions = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  margin: -4px -10px -4px -4px;
  &:empty {
    display: none;
  }
`;
